/**
 * Chinese labels for device-related field keys returned by the catalog
 * commands (`device.info`, `device.screen`, `device.battery`, `net.state`,
 * `device.uptime`, `app.dumpActivity`, etc.).
 *
 * `Dashboard` overlays these next to the original camelCase keys so the user
 * sees `brand · 品牌` instead of just `brand`.
 */
export const FIELD_LABELS: Record<string, string> = {
  /* device.info */
  brand: '品牌',
  manufacturer: '厂商',
  model: '型号',
  device: '设备代号',
  product: '产品代号',
  release: '系统版本',
  sdk: 'SDK 等级',
  abi: '架构',
  abi32: 'ABI32',
  abi64: 'ABI64',
  fingerprint: '指纹',
  serial: '序列号',
  bootId: 'Boot ID',
  buildId: 'Build ID',
  buildType: '构建类型',
  buildTags: '构建标签',
  buildDate: '构建时间',
  hardware: '硬件',
  board: '主板',
  bootloader: 'Bootloader',
  baseband: '基带',
  kernel: '内核',
  androidId: 'Android ID',

  /* device.screen */
  densityDpi: 'DPI',
  density: '密度',
  widthPx: '宽 (px)',
  heightPx: '高 (px)',
  realWidthPx: '真实宽 (px)',
  realHeightPx: '真实高 (px)',
  rotation: '旋转',
  refreshRateHz: '刷新率 (Hz)',
  orientation: '方向',
  scale: '缩放',

  /* device.battery */
  level: '电量',
  scale_: '量程',
  temperature: '温度 (0.1℃)',
  voltage: '电压 (mV)',
  current: '电流 (mA)',
  technology: '电池工艺',
  acPowered: '交流供电',
  usbPowered: 'USB 供电',
  wirelessPowered: '无线供电',
  status: '状态',
  health: '健康度',
  plug: '插入类型',
  charging: '充电中',
  chargeCounter: '充电计数 (μAh)',
  capacity: '容量',
  energyCounter: '能量计数 (nWh)',

  /* device.uptime */
  uptimeSeconds: '开机秒数',
  uptimeReadable: '开机时长',

  /* device.locale */
  locale: '语言区域',
  timezone: '时区',
  ime: '当前输入法',
  language: '语言',
  country: '国家',

  /* net.state / net.ip */
  hasInternet: '联网中',
  online: '在线',
  wifi: 'WiFi',
  mobile: '移动网络',
  ssid: 'WiFi 名称',
  bssid: 'BSSID',
  rssi: '信号强度',
  activeType: '网络类型',
  ipv4: 'IPv4',
  ipv4All: '全部 IPv4',
  ipv6: 'IPv6',
  gateway: '网关',
  dns: 'DNS',
  mac: 'MAC',

  /* app.dumpActivity */
  topActivity: '前台 Activity',
  topResumedActivity: '已恢复 Activity',
  pid: 'PID',
  processName: '进程名',
  package: '包名',
  activity: 'Activity',
  taskId: '任务 ID',

  /* perf.battery / perf.cpuinfo / perf.meminfo */
  totalKb: '总内存 (KB)',
  freeKb: '空闲 (KB)',
  availableKb: '可用 (KB)',
  buffersKb: 'Buffers (KB)',
  cachedKb: 'Cached (KB)',
  cpuCount: 'CPU 核数',
  cpuUsagePct: 'CPU 占用 (%)',
  cpuFreqs: 'CPU 频率',
  governor: '调度器',
  totalCapacityMs: '总用电 (ms)',
  computeCapacityMs: '计算耗电 (ms)',

  /* fs.diskUsage / fs.stat */
  size: '大小',
  totalBytes: '总容量 (B)',
  usedBytes: '已用 (B)',
  freeBytes: '可用 (B)',
  mtime: '修改时间',
  permissions: '权限',
  type: '类型',

  /* device.info+ identity extras */
  abiList: 'ABI 列表',
  firstApiLevel: '首发 API',
  oemSkin: '系统皮肤',
  oemSkinVersion: '皮肤版本',

  /* device.cpu */
  cores: 'CPU 核数',
  onlineCores: '在线核数',
  soc: 'SoC',
  freqMinMhz: '最小频率 (MHz)',
  freqMaxMhz: '最大频率 (MHz)',
  freqCurMhz: '当前频率 (MHz)',
  load1: '1分钟负载',
  load5: '5分钟负载',
  load15: '15分钟负载',
  revision: 'CPU 修订',

  /* device.gpu */
  vendor: '厂商',
  renderer: '渲染器',
  glesVersion: 'GLES 版本',
  eglVersion: 'EGL 版本',

  /* device.memory */
  memTotalKb: '内存总量 (KB)',
  memFreeKb: '空闲 (KB)',
  memAvailableKb: '可用 (KB)',
  swapTotalKb: 'Swap 总量 (KB)',
  swapFreeKb: 'Swap 空闲 (KB)',
  shmemKb: '共享内存 (KB)',
  anonPagesKb: '匿名页 (KB)',
  zramKb: 'ZRAM 总量 (KB)',
  dalvikHeapSizeMb: 'Dalvik 堆 (MB)',
  dalvikHeapGrowthLimitMb: 'Dalvik 堆上限 (MB)',
  dalvikHeapMinFreeMb: 'Dalvik 最小空闲 (MB)',
  dalvikHeapMaxFreeMb: 'Dalvik 最大空闲 (MB)',

  /* device.display */
  supportedRefreshRates: '可选刷新率',
  brightness: '当前亮度',
  brightnessMax: '亮度上限',
  hdrTypes: 'HDR 类型',
  physicalWidth: '物理宽 (px)',
  physicalHeight: '物理高 (px)',
  overrideWidth: '覆盖宽 (px)',
  overrideHeight: '覆盖高 (px)',

  /* device.sensors */
  sensorCount: '传感器数',
  count: '数量',
  buckets: '分类',
  sensors: '传感器列表',
  sensorList: '传感器明细',
  motion: '运动类',
  position: '姿态类',
  environment: '环境类',
  biometric: '生物类',
  light: '光线类',
  other: '其他',
  maxRange: '量程',
  resolution: '分辨率',
  powerMa: '功耗 (mA)',
  minDelayUs: '最小延迟 (μs)',
  maxDelayUs: '最大延迟 (μs)',
  reportingMode: '触发模式',
  minRateHz: '最小频率 (Hz)',
  maxRateHz: '最大频率 (Hz)',
  fifoMax: 'FIFO 容量',
  fifoReserved: 'FIFO 预留',
  wakeUp: '唤醒源',
  permission: '所需权限',

  /* device.cameras */
  cameras: '相机列表',
  cameraList: '相机明细',
  facing: '朝向',
  characteristics: '特性',
  id: 'ID',
  hasFlash: '闪光灯',
  halDeviceVersion: 'HAL 版本',
  halMajor: 'HAL 主版本',
  resourceCost: '资源开销',
  conflictingIds: '互斥相机',
  focalLengthsMm: '焦距 (mm)',
  aperturesF: '光圈',
  maxJpegSize: '最大 JPEG 分辨率',
  physicalSensorSizeMm: '传感器物理尺寸 (mm)',
  pixelArraySize: '像素阵列',
  activeArraySize: '有效成像区',
  maxDigitalZoom: '最大数字变焦',
  aeCompensationRange: '曝光补偿范围 (EV)',
  capabilities: '能力',

  /* device.build */
  kernelVersion: '内核版本',
  kernelArch: '内核架构',
  buildHost: '构建主机',
  buildUser: '构建用户',
  buildIncremental: '增量号',
  codename: '版本代号',
  securityPatch: '安全补丁',
  treble: 'Treble',
  verifiedBootState: '验证启动',
  flashLocked: 'Bootloader 已锁',
  cryptoState: '加密状态',
  cryptoType: '加密类型',
  bootReason: '启动原因',

  /* device.runtime */
  selinux: 'SELinux',
  usbMode: 'USB 模式',
  adbSecure: 'ADB 验证',
  devOptions: '开发者选项',
  mockLocation: '模拟位置',
  bootCompletedAt: '启动完成时间',
  is24Hour: '24 小时制',

  /* device.storage */
  mounts: '挂载点',
  mount: '挂载点',
  fsType: '文件系统',
  sizeKb: '总量 (KB)',
  usedKb: '已用 (KB)',
  usedPercent: '使用率 (%)',
  bootDevice: '启动设备',
  storageType: '存储类型',
  partitions: '分区表',
  sdcardPresent: '存在 SD 卡',
  usbConfig: 'USB 配置',

  /* net.connectivity / net.cellular / net.bluetooth */
  gatewayV4: '默认网关',
  activeIface: '活动接口',
  mtu: 'MTU',
  dnsList: 'DNS 服务器',
  operator: '运营商',
  mcc: 'MCC',
  mnc: 'MNC',
  networkType: '网络类型',
  signalStrengthDbm: '信号强度 (dBm)',
  signalLevel: '信号格数',
  dataState: '数据状态',
  simState: 'SIM 状态',
  enabled: '已启用',
  name: '名称',
  address: '地址',
  scanMode: '扫描模式',
  pairedCount: '已配对数',

  /* device.battery extensions */
  currentMa: '电流 (mA)',
  chargeCounterUah: '电量计数 (μAh)',
  chargeFullUah: '满电容量 (μAh)',
  chargeFullDesignUah: '设计容量 (μAh)',
  cycleCount: '循环次数',
  healthPercent: '电池健康 (%)',
  chargeWatts: '充电功率 (W)',
};

export function fieldLabel(key: string): string | undefined {
  return FIELD_LABELS[key];
}
